import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useMotivation } from '../context/MotivationContext';
import MotivationForm from './MotivationForm';

const AiMotivation = () => {
  const { motivationalContent, isLoading, error, history, clearMotivation } = useMotivation();
  const [showHistory, setShowHistory] = useState(false);

  const toggleHistory = () => {
    setShowHistory(prev => !prev);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <MotivationContainer>
      <MotivationForm />

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {motivationalContent && !isLoading && (
        <ResultContainer>
          <ResultHeader>
            <ResultTitle>Reality Check</ResultTitle>
            <CloseButton onClick={clearMotivation}>&times;</CloseButton>
          </ResultHeader>
          <MotivationContent>
            {motivationalContent.split('\n\n').map((paragraph, index) => (
              <Paragraph key={index}>{paragraph}</Paragraph>
            ))}
          </MotivationContent>
        </ResultContainer>
      )}

      {history.length > 0 && (
        <HistorySection>
          <HistoryToggle onClick={toggleHistory}>
            {showHistory ? 'Hide Previous Reality Checks' : 'Show Previous Reality Checks'}
          </HistoryToggle>

          {showHistory && (
            <HistoryList>
              {history.map((item) => (
                <HistoryItem key={item.id}>
                  <HistoryItemHeader>
                    <HistoryItemPrompt>"{item.prompt}"</HistoryItemPrompt>
                    <HistoryItemDate>{formatDate(item.date)}</HistoryItemDate>
                  </HistoryItemHeader>
                  <HistoryItemContent>
                    {item.content.split('\n\n').map((paragraph, index) => (
                      <Paragraph key={index}>{paragraph}</Paragraph>
                    ))}
                  </HistoryItemContent>
                </HistoryItem>
              ))}
            </HistoryList>
          )}
        </HistorySection>
      )}
    </MotivationContainer>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MotivationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  animation: ${css`${fadeIn} 0.5s ease-in-out`};
`;

const ErrorMessage = styled.div`
  background: rgba(170, 51, 51, 0.2);
  border: 1px solid #a33;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  color: #a33;
  width: 100%;
  text-align: center;
`;

const ResultContainer = styled.div`
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border-radius: 8px;
  padding: 2rem;
  margin: 1.5rem 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2), 0 0 15px rgba(212, 175, 55, 0.3) inset;
  width: 100%;
  border: 2px solid var(--color-gold);
  animation: ${css`${fadeIn} 0.5s ease-in-out`};
  position: relative;

  &::before {
    content: '"';
    position: absolute;
    top: -15px;
    left: 20px;
    font-size: 60px;
    font-family: var(--font-primary);
    color: var(--color-gold);
    opacity: 0.3;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-gold);
  padding-bottom: 0.5rem;
`;

const ResultTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  color: var(--color-gold);
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: var(--color-gold);
  }
`;

const MotivationContent = styled.div`
  color: var(--color-text);
  font-family: var(--font-secondary);
  line-height: 1.6;
  font-size: 1.1rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  font-weight: 500;

  &:last-child {
    margin-bottom: 0;
  }

  &:first-child {
    color: var(--color-gold);
    font-size: 1.2rem;
  }
`;

const HistorySection = styled.div`
  width: 100%;
  margin-top: 2rem;
`;

const HistoryToggle = styled.button`
  background: transparent;
  border: 1px solid var(--color-secondary);
  border-radius: 30px;
  padding: 0.6rem 1.2rem;
  color: var(--color-secondary);
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    border-color: var(--color-gold);
    color: var(--color-gold);
  }
`;

const HistoryList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: ${css`${fadeIn} 0.3s ease-in-out`};
`;

const HistoryItem = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--color-secondary);
  border-radius: 8px;
  padding: 1.5rem;
`;

const HistoryItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--color-secondary);
  padding-bottom: 0.5rem;
`;

const HistoryItemPrompt = styled.p`
  color: var(--color-gold);
  font-style: italic;
  margin: 0;
  font-size: 0.9rem;
  flex: 1;
`;

const HistoryItemDate = styled.span`
  color: var(--color-secondary);
  font-size: 0.8rem;
  margin-left: 1rem;
`;

const HistoryItemContent = styled.div`
  color: var(--color-text);
  font-size: 0.9rem;
`;

export default AiMotivation;
